import axios from 'axios';

const horseApiUrl = process.env.REACT_APP_HORSE_API_URL || 'https://testeint.apihorse.nexxusone.com.br';
const horseApiKey = process.env.REACT_APP_HORSE_API_KEY;

const HorseCheckout = axios.create({
  baseURL: horseApiUrl,
});

export interface CriarPixPayload {
  valor: number;
  /** Usado no histórico/local se a API não retornar expiração */
  expiracao?: number;
}

export interface PixPagarBody {
  metodo: 'pix';
  valor: number;
}

export interface PixNormalizado {
  id: string;
  criptographyTx_id: string;
  copiaECola: string;
  /** EMV copia e cola (preferido para gerar QR) */
  qrcode_url: string;
  pixLocation?: string;
  stream_url?: string;
  expiracao: number;
}

/** Resposta: { sucesso, dados: { pix: { txid, pixLocation, pixCopiaECola, stream_url } } } */
function extractPixNode(dados: Record<string, unknown>): Record<string, unknown> | null {
  if (!dados) return null;
  const pix = dados.pix;
  if (pix && typeof pix === 'object' && !Array.isArray(pix)) {
    return pix as Record<string, unknown>;
  }
  return dados;
}

function ensureAbsoluteUrl(url?: string): string | undefined {
  if (!url?.trim()) return undefined;
  const trimmed = url.trim().replace(/\\\//g, '/');
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

function isPixEmvPayload(value: string): boolean {
  const v = value.trim();
  return v.startsWith('000201') || v.startsWith('00020');
}

function pickString(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim().replace(/\\\//g, '/');
  }
  return '';
}

function normalizeHorseResponse(
  dados: Record<string, unknown>,
  expiracaoFallback: number
): PixNormalizado | null {
  const pix = extractPixNode(dados);
  if (!pix) return null;

  const txid = pickString(pix.txid, pix.Txid);
  const copiaECola = pickString(
    pix.pixCopiaECola,
    pix.pix_copia_e_cola,
    pix.copiaECola,
    pix.copia_e_cola
  );
  const pixLocationRaw = pickString(pix.pixLocation, pix.pix_location);
  const pixLocation = ensureAbsoluteUrl(pixLocationRaw);
  const stream_url = ensureAbsoluteUrl(pickString(pix.stream_url, pix.streamUrl));

  const expiracaoPix = pix.expiracao ?? pix.expiracaoSegundos;
  const expiracaoDados = dados.expiracao;
  const expiracao =
    typeof expiracaoPix === 'number' && expiracaoPix > 0
      ? expiracaoPix
      : typeof expiracaoDados === 'number' && expiracaoDados > 0
        ? expiracaoDados
        : expiracaoFallback;

  const emvForQr = isPixEmvPayload(copiaECola)
    ? copiaECola
    : isPixEmvPayload(pixLocationRaw)
      ? pixLocationRaw
      : copiaECola;

  if (!txid && !emvForQr) {
    return null;
  }

  return {
    id: txid,
    criptographyTx_id: txid,
    copiaECola: emvForQr || copiaECola,
    qrcode_url: emvForQr,
    pixLocation,
    stream_url,
    expiracao,
  };
}

function getErrorMessage(error: any): string {
  const erro = error.response?.data?.erro;
  if (erro?.mensagem) return erro.mensagem;
  if (error.response?.data?.message) return error.response.data.message;
  return error.message || 'Erro ao criar Pix';
}

async function criarPix(linkPagamentoId: string, data: CriarPixPayload) {
  if (!horseApiKey) {
    throw new Error('REACT_APP_HORSE_API_KEY não configurada no .env');
  }

  const expiracaoFallback = data.expiracao ?? 10800;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': horseApiKey,
    'x-link': linkPagamentoId,
  };

  const body: PixPagarBody = {
    metodo: 'pix',
    valor: data.valor,
  };

  try {
    const response = await HorseCheckout.post('/link-pagamento/pagar?=', body, { headers });

    const raw = response.data;
    const dados = raw?.dados;
    if (!raw?.sucesso || !dados) {
      throw new Error(getErrorMessage({ response: { data: raw } }) || 'Resposta inválida da API Horse.');
    }

    const normalized = normalizeHorseResponse(dados, expiracaoFallback);
    if (!normalized?.copiaECola && !normalized?.qrcode_url) {
      throw new Error('PIX gerado sem código copia e cola.');
    }

    return { data: normalized };
  } catch (error: any) {
    console.error('Erro ao criar Pix:', error.message);
    throw new Error(getErrorMessage(error));
  }
}

export { criarPix };
